import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Link, Menu, MenuItem, ListItemIcon, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/layout/Layout';
import JobFormDrawer from '../components/jobs/JobFormDrawer';
import WorkflowCard from '../components/jobs/WorkflowCard';
import VisitsCard from '../components/jobs/VisitsCard';
import FilesCard from '../components/jobs/FilesCard';
import { ACCEPT_IMAGE, ACCEPT_PDF, FileViewer, PdfPreview, PdfRow, PhotoThumb, useFilePicker, useUploader } from '../components/jobs/files';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import PaymentsCard from '../components/jobs/PaymentsCard';
import ProformasCard from '../components/jobs/ProformasCard';
import JobHistoryCard from '../components/jobs/JobHistoryCard';
import AssigneeSelect from '../components/jobs/AssigneeSelect';
import LocationDialog from '../components/map/LocationDialog';
import JobLocationCard from '../components/map/JobLocationCard';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { CategoryTag, InfoRow, OwnerList, SectionCard, StatusPill } from '../components/common/ui';
import StatusFlow from '../components/common/StatusFlow';
import { STATUS, STATUS_ORDER } from '../styles/tokens';
import JobService from '../services/JobService';
import { errorMessage } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { useMoneyLock } from '../contexts/MoneyContext';
import { fmtDate, todayIso, fmtTimeRange } from '../utils/format';
import { hasInvestor, hasParcelNumbers, isCadastre, scheduledLabel, workflowOf } from '../utils/workflows';
import { usePageRefresh } from '../components/layout/pullToRefresh';

export default function JobDetailPage() {
    const { id } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const { isAdmin } = useAuth();
    const money = useMoneyLock(`job:${id}`);
    const [job, setJob] = useState(null);
    const [notFound, setNotFound] = useState(false);
    // null = closed, 'all' = the full form, or one box: 'details' | 'survey' | 'description'.
    const [editSection, setEditSection] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const statusFlow = useRef(null);
    const [locationOpen, setLocationOpen] = useState(false);

    const load = useCallback(() => {
        return JobService.get(id).then(setJob).catch((err) => {
            if (err?.response?.status === 404) setNotFound(true);
            else showToast(t('common.loadError'), 'error');
        });
    }, [id, showToast, t, money.visible]); // eslint-disable-line react-hooks/exhaustive-deps -- reload when prices are shown/hidden
    useEffect(() => { setJob(null); setNotFound(false); load(); }, [load]);

    // Files and visits load separately from the job so uploads can refresh them on their own.
    const [files, setFiles] = useState([]);
    const [visits, setVisits] = useState([]);
    const [viewer, setViewer] = useState(null);
    const [fileToDelete, setFileToDelete] = useState(null);
    const [visitAddRequest, setVisitAddRequest] = useState(0);
    const loadWork = useCallback(() => {
        return Promise.all([
            JobService.files(id).then(setFiles).catch(() => {}),
            JobService.visits(id).then(setVisits).catch(() => {}),
        ]);
    }, [id]);
    useEffect(() => { setFiles([]); setVisits([]); loadWork(); }, [loadWork]);
    const refreshAll = useCallback(() => { load(); loadWork(); }, [load, loadWork]);
    // Pull-to-refresh: the job, its files and visits, and (via historyVersion) its notes and activity.
    const [historyVersion, setHistoryVersion] = useState(0);
    usePageRefresh(() => { setHistoryVersion(v => v + 1); return Promise.all([load(), loadWork()]); });
    const { upload, progress, uploading } = useUploader(id, refreshAll);
    // Other jobs on the same parcel (parcel + KO, municipality when both have one), reloaded when those change.
    const [sameParcel, setSameParcel] = useState([]);
    const parcelKey = job ? `${job.municipality}|${job.cadastralZone}|${job.parcelNumber}` : '';
    useEffect(() => {
        if (!parcelKey) return;
        JobService.sameParcel(id).then(setSameParcel).catch(() => setSameParcel([]));
    }, [id, parcelKey]);
    // Property sheet: one PDF, or photos of its pages taken on site (each photo is one more page).
    const [sheetInput, pickSheet] = useFilePicker((fl) => upload(fl, { stepCode: 'PROPERTY_SHEET' }), { accept: ACCEPT_PDF, multiple: false });
    const [sheetCameraInput, takeSheetPhoto] = useFilePicker((fl) => upload(fl, { stepCode: 'PROPERTY_SHEET' }), { accept: ACCEPT_IMAGE, multiple: false, capture: true });

    const deleteFile = async () => {
        try {
            setJob(await JobService.deleteFile(job.id, fileToDelete.id));
            showToast(t('files.deleted'), 'success');
            setViewer(null);
            loadWork();
        } catch (err) {
            showToast(errorMessage(err, t('common.error')), 'error');
        } finally {
            setFileToDelete(null);
        }
    };

    // Confirmation, toast and Undo are handled by StatusFlow; errors propagate to it.
    const changeStatus = async (status) => setJob(await JobService.setStatus(job.id, status));

    const remove = async () => {
        try {
            await JobService.remove(job.id);
            showToast(t('jobs.toast.deleted'), 'success');
            navigate('/jobs', { replace: true });
        } catch (err) {
            showToast(errorMessage(err, t('common.error')), 'error');
            setDeleteOpen(false);
        }
    };

    if (notFound) {
        return (
            <Layout>
                <Box sx={{ textAlign: 'center', mt: 10 }}>
                    <Typography variant="h6" fontWeight={700}>{t('jobs.notFound')}</Typography>
                    <Button sx={{ mt: 2 }} onClick={() => navigate('/jobs')}>{t('jobs.backToJobs')}</Button>
                </Box>
            </Layout>
        );
    }
    if (!job) return <Layout><Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box></Layout>;

    // Overdue = the work on site is still open past its date (office work and the cadastre may keep the job open longer).
    const overdue = job.scheduledDate && job.scheduledDate < todayIso() && !job.fieldDone;
    const workflow = workflowOf(job.category);
    const sideLabel = { fontSize: 11.5, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.5 };
    const sheetFile = files.find(f => f.stepCode === 'PROPERTY_SHEET' && f.kind === 'PDF');
    const sheetPhotos = files.filter(f => f.stepCode === 'PROPERTY_SHEET' && f.kind === 'IMAGE');
    // Each box edits on its own (same form, only that group shown).
    const editBox = (name) => (
        <Button size="small" startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />} onClick={() => setEditSection(name)}>{t('common.edit')}</Button>
    );
    const jobLabel = (s) => t(`status.${s}`);
    const nextSteps = {
        RECEIVED: [{ to: 'IN_PROGRESS', label: t('jobs.flow.start'), icon: <PlayArrowRoundedIcon />, color: 'warning' }],
        IN_PROGRESS: [{ to: 'DONE', label: t('jobs.flow.markDone'), icon: <TaskAltIcon />, color: 'success' }],
    }[job.status] || [];
    const confirmOf = (to) => {
        const vars = { number: job.number || `“${job.title}”`, title: job.title, from: jobLabel(job.status), to: jobLabel(to), interpolation: { escapeValue: false } };
        const forward = STATUS_ORDER.indexOf(to) > STATUS_ORDER.indexOf(job.status);
        const open = job.progress ? job.progress.total - job.progress.done : 0;
        let text = forward ? t(`jobs.flow.confirm.${to}.text`, vars) : t('status.backText', vars);
        // Finishing with checklist steps still open is allowed, but say so.
        if (to === 'DONE' && open > 0) text += ' ' + t('jobs.flow.openSteps', { count: open });
        return { title: t(`jobs.flow.confirm.${to}.title`, vars), text, confirmLabel: t(`jobs.flow.confirm.${to}.button`) };
    };
    const openFile = (list, index) => setViewer({ list, index });

    return (
        <Layout>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ color: '#64748B', mb: 1, ml: -1 }}>
                    {t('common.back')}
                </Button>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#94A3B8', letterSpacing: 0.4 }}>{job.number}</Typography>
                            <CategoryTag category={job.category} />
                        </Box>
                        {/* Identity only (number, category, title); the client with contact details is in Details. */}
                        <Typography variant="h5" fontWeight={700} sx={{ color: '#1a2332', letterSpacing: -0.3, wordBreak: 'break-word' }}>{job.title}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Button variant="outlined" startIcon={<EditOutlinedIcon />} onClick={() => setEditSection('all')}>{t('common.edit')}</Button>
                        <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ border: '1px solid #E2E8F0', borderRadius: '10px' }} aria-label={t('common.more')}>
                            <MoreHorizIcon />
                        </IconButton>
                        <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                            <MenuItem onClick={() => { setMenuAnchor(null); setDeleteOpen(true); }} sx={{ color: '#DC2626', fontSize: 14 }}>
                                <ListItemIcon sx={{ color: '#DC2626' }}><DeleteOutlineIcon fontSize="small" /></ListItemIcon>{t('jobs.delete')}
                            </MenuItem>
                        </Menu>
                    </Box>
                </Box>
            </Box>

            {/*
              * Wide left column: details and survey details side by side first (the job's basic facts), then checklist,
              * files and history. Narrow right column: status card, location map and payments. Phones get one column in an
              * explicit order (the columns dissolve with display: contents): status, details, survey details, map,
              * checklist + files, history, payments.
              */}
            <Box sx={{ display: { xs: 'flex', lg: 'grid' }, flexDirection: 'column', gridTemplateColumns: 'minmax(0, 1fr) 380px', gap: 2.5, alignItems: { xs: 'stretch', lg: 'start' },
                       '& .slot > .MuiPaper-root': { m: 0 } }}>
                <Box sx={{ display: { xs: 'contents', lg: 'flex' }, flexDirection: 'column', gap: 2.5, minWidth: 0 }}>
                    {/* The job's basic facts first: details and survey details side by side. */}
                    <Box sx={{ order: 2, display: { xs: 'contents', md: 'grid' }, gridTemplateColumns: '1fr 1fr', gap: 2.5, alignItems: 'start' }}>
                        <Box className="slot" sx={{ order: 2, minWidth: 0 }}>
                            <SectionCard title={t('jobs.sections.details')} action={editBox('details')}>
                                <Box sx={{ px: 2.5, py: 1 }}>
                                    <InfoRow label={t('jobs.fields.client')}>
                                        <Link component={RouterLink} to={`/clients/${job.client.id}`} underline="hover" sx={{ color: '#427898', fontWeight: 600 }}>{job.client.name}</Link>
                                        {job.client.phone && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#64748B', fontSize: 12.5, mt: 0.25 }}>
                                                <PhoneOutlinedIcon sx={{ fontSize: 14 }} />
                                                <Link href={`tel:${job.client.phone}`} underline="hover" color="inherit">{job.client.phone}</Link>
                                            </Box>
                                        )}
                                        {job.client.email && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#64748B', fontSize: 12.5, mt: 0.25, minWidth: 0 }}>
                                                <MailOutlineIcon sx={{ fontSize: 14, flexShrink: 0 }} />
                                                <Link href={`mailto:${job.client.email}`} underline="hover" color="inherit" noWrap>{job.client.email}</Link>
                                            </Box>
                                        )}
                                    </InfoRow>
                                    {hasInvestor(job.category) && <InfoRow label={t('jobs.fields.investor')}>{job.investor}</InfoRow>}
                                    <InfoRow label={t('jobs.fields.number')}>{job.number}</InfoRow>
                                    <InfoRow label={t('jobs.fields.cadastreCaseNumber')}>{job.cadastreCaseNumber}</InfoRow>
                                    <InfoRow label={scheduledLabel(t, job.category)}>
                                        {job.scheduledDate ? (
                                            <>
                                                <Box component="span" sx={{ color: overdue ? '#DC2626' : 'inherit', fontWeight: overdue ? 700 : 400 }}>{fmtDate(job.scheduledDate)}{job.scheduledStart ? `, ${fmtTimeRange(job)}` : ''}{overdue ? ` · ${t('jobs.overdue')}` : ''}</Box>
                                                {job.fieldDone && job.status !== 'DONE' && <Box component="span" sx={{ color: '#059669', fontWeight: 600 }}> · ✓ {t('jobs.fieldDone')}</Box>}
                                            </>
                                        ) : <Box component="span" sx={{ color: '#94A3B8' }}>{t('jobs.notScheduled')}</Box>}
                                    </InfoRow>
                                    {isAdmin && job.quoteId && (
                                        <InfoRow label={t('quotes.fromQuote')}>
                                            <Link component={RouterLink} to={`/quotes/${job.quoteId}`} underline="hover" sx={{ color: '#427898', fontWeight: 600 }}>{t('quotes.viewQuote')}</Link>
                                        </InfoRow>
                                    )}
                                    <InfoRow label={t('jobs.fields.processedOn')}>{job.processedOn ? fmtDate(job.processedOn) : null}</InfoRow>
                                    {/* Cadastre categories show the submission date in their checklist. */}
                                    {!isCadastre(job.category) && <InfoRow label={t('jobs.fields.submittedOn')}>{job.steps?.SUBMITTED ? fmtDate(job.steps.SUBMITTED) : null}</InfoRow>}
                                    <InfoRow label={t('jobs.fields.created')}>{fmtDate(job.createdAt)}</InfoRow>
                                    {job.completedAt && <InfoRow label={t('jobs.fields.completed')}>{fmtDate(job.completedAt)}</InfoRow>}
                                </Box>
                                {/* Description belongs to the details (edited with them). */}
                                <Box sx={{ px: 2.5, py: 1.75, borderTop: '1px solid #F1F5F9' }}>
                                    <Typography sx={sideLabel}>{t('jobs.fields.description')}</Typography>
                                    <Typography sx={{ fontSize: 13.5, color: job.description ? '#334155' : '#94A3B8', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {job.description || t('jobs.noDescription')}
                                    </Typography>
                                </Box>
                            </SectionCard>
                        </Box>
                        <Box className="slot" sx={{ order: 3, minWidth: 0 }}>
                            <SectionCard title={t('jobs.sections.survey')} action={editBox('survey')}>
                                <Box sx={{ px: 2.5, py: 1 }}>
                                    <InfoRow label={t('jobs.fields.ownerName')}>{job.ownerName}</InfoRow>
                                    {job.coOwners?.length > 0 && <InfoRow label={t('jobs.fields.coOwners')}><OwnerList names={job.coOwners} /></InfoRow>}
                                    <InfoRow label={t('jobs.fields.municipality')}>{job.municipality}</InfoRow>
                                    <InfoRow label={t('jobs.fields.cadastralZone')}>{job.cadastralZone}</InfoRow>
                                    <InfoRow label={t('jobs.fields.propertySheet')}>{job.propertySheet}</InfoRow>
                                    <InfoRow label={t('jobs.fields.parcelNumber')}>{job.parcelNumber}</InfoRow>
                                    {hasParcelNumbers(job.category) && <InfoRow label={t('jobs.fields.parentParcel')}>{job.parentParcel}</InfoRow>}
                                    {hasParcelNumbers(job.category) && <InfoRow label={t('jobs.fields.newParcels')}>{job.newParcels}</InfoRow>}
                                </Box>
                                {/* Optional property sheet (list nepokretnosti): one PDF, or photos of its pages taken on site. */}
                                <Box sx={{ px: 2.5, py: 1.25, borderTop: '1px solid #F1F5F9' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 0.5, rowGap: 0.25, flexWrap: 'wrap', ml: -0.75 }}>
                                        <Typography sx={{ ...sideLabel, mb: 0, width: '100%', ml: 0.75 }}>{t('survey.propertySheetFile')}</Typography>
                                        {sheetInput}{sheetCameraInput}
                                        <Button size="small" startIcon={<PhotoCameraOutlinedIcon sx={{ fontSize: 16 }} />} onClick={takeSheetPhoto} disabled={uploading} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                                            {sheetPhotos.length ? t('survey.addSheetPage') : t('survey.photographSheet')}
                                        </Button>
                                        <Button size="small" startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 16 }} />} onClick={pickSheet} disabled={uploading} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                                            {sheetFile ? t('workflow.replacePdf') : t('survey.attachSheet')}
                                        </Button>
                                    </Box>
                                    {sheetPhotos.length > 0 && (
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                                            {sheetPhotos.map((f, i) => <PhotoThumb key={f.id} owner={job.id} file={f} size={76} onOpen={() => openFile(sheetPhotos, i)} />)}
                                        </Box>
                                    )}
                                    {sheetFile && (
                                        <>
                                            {/* First-page preview (keeps the box level with Details); click opens the PDF. */}
                                            <Box sx={{ mt: 1 }}>
                                                <PdfPreview jobId={job.id} file={sheetFile} height={140} onOpen={() => openFile([sheetFile], 0)} />
                                            </Box>
                                            <PdfRow owner={job.id} file={sheetFile} dense onOpen={() => openFile([sheetFile], 0)} onDelete={() => setFileToDelete(sheetFile)} />
                                        </>
                                    )}
                                </Box>
                                {sameParcel.length > 0 && (
                                    <Box sx={{ px: 2.5, py: 1.25, borderTop: '1px solid #F1F5F9' }}>
                                        <Typography sx={sideLabel}>{t('jobs.sameParcel', { count: sameParcel.length })}</Typography>
                                        {sameParcel.map(p => (
                                            <Box key={p.id} component={RouterLink} to={`/jobs/${p.id}`} sx={{
                                                display: 'flex', alignItems: 'center', gap: 1, py: 0.6, textDecoration: 'none', color: 'inherit',
                                                borderRadius: '6px', '&:hover': { bgcolor: '#F8FAFC' },
                                            }}>
                                                <Box sx={{ width: 4, alignSelf: 'stretch', borderRadius: 4, bgcolor: p.category?.color || '#CBD5E1', flexShrink: 0 }} />
                                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                                    <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, color: '#0F172A' }}>{[p.number, p.title].filter(Boolean).join(' · ')}</Typography>
                                                    <Typography noWrap sx={{ fontSize: 11.5, color: '#94A3B8' }}>{[p.category?.name, fmtDate(p.createdAt)].filter(Boolean).join(' · ')}</Typography>
                                                </Box>
                                                <StatusPill status={p.status} size="sm" />
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </SectionCard>
                        </Box>
                    </Box>

                    <Box className="slot" sx={{ order: 5, display: 'flex', flexDirection: 'column', gap: 2.5, minWidth: 0 }}>
                        {workflow && (
                            <WorkflowCard job={job} files={files} visits={visits} onJobChange={setJob} onMarkDone={() => statusFlow.current?.request('DONE')}
                                          upload={upload} uploading={uploading} openFile={openFile} askDelete={setFileToDelete}
                                          onShowVisits={() => setVisitAddRequest(n => n + 1)} />
                        )}
                        {job.category?.workflow === 'BUILDING_STAKING' && (
                            <VisitsCard job={job} visits={visits} files={files} onChanged={refreshAll} addRequest={visitAddRequest}
                                        upload={upload} uploading={uploading} openFile={openFile} askDelete={setFileToDelete} />
                        )}
                        <FilesCard owner={job.id} files={files} visits={visits} upload={upload} progress={progress}
                                   openFile={openFile} askDelete={setFileToDelete} />
                    </Box>
                    <Box className="slot" sx={{ order: 6, minWidth: 0 }}>
                        <JobHistoryCard jobId={job.id} reloadKey={`${job.updatedAt}-${files.length}-${visits.length}-${money.visible}-${historyVersion}`} />
                    </Box>
                </Box>

                <Box sx={{ display: { xs: 'contents', lg: 'flex' }, flexDirection: 'column', gap: 2.5, minWidth: 0 }}>
                    {/* Status and crew; money lives only in the Payments card. */}
                    <Box className="slot" sx={{ order: 1 }}>
                        <SectionCard title={t('jobs.fields.status')}>
                            <Box sx={{ px: 2.5, py: 1.75, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                    <StatusFlow ref={statusFlow} status={job.status} order={STATUS_ORDER} styles={STATUS} labelOf={jobLabel}
                                                nextSteps={nextSteps} confirmOf={confirmOf} onChange={changeStatus}
                                                successOf={(s) => t('jobs.toast.statusChanged', { status: jobLabel(s) })} />
                                </Box>
                                <Box>
                                    <Typography sx={sideLabel}>{t('jobs.fields.assignees')}</Typography>
                                    <AssigneeSelect job={job} editable onChange={setJob} />
                                </Box>
                            </Box>
                        </SectionCard>
                    </Box>

                    {/* Kept at the bottom: amounts stay hidden behind the password anyway. */}
                    {/* The map fills the side column next to the checklist instead of stretching the survey box. */}
                    <Box className="slot" sx={{ order: 4 }}>
                        <JobLocationCard job={job} onEdit={() => setLocationOpen(true)} />
                    </Box>
                    {isAdmin && <Box className="slot" sx={{ order: 7 }}><PaymentsCard job={job} money={money} onJobChange={setJob} /></Box>}
                    {isAdmin && <Box className="slot" sx={{ order: 8 }}><ProformasCard job={job} money={money} /></Box>}
                </Box>
            </Box>

            <JobFormDrawer open={!!editSection} job={job} money={money} section={editSection === 'all' ? undefined : editSection}
                           onClose={() => setEditSection(null)} onSaved={(j) => { setJob(j); setEditSection(null); }} />
            <FileViewer owner={job.id} files={viewer?.list || []} index={viewer?.index ?? null}
                        onIndex={(i) => setViewer(v => ({ ...v, index: i }))} onClose={() => setViewer(null)} onDelete={setFileToDelete} />
            <ConfirmDialog open={!!fileToDelete} title={t('files.deleteTitle')} text={t('files.deleteText', { name: fileToDelete?.name })}
                           onConfirm={deleteFile} onClose={() => setFileToDelete(null)} />
            <LocationDialog open={locationOpen} job={job} onClose={() => setLocationOpen(false)} onSaved={(j) => { setJob(j); setLocationOpen(false); }} />
            <ConfirmDialog open={deleteOpen} title={job.number ? t('jobs.deleteTitle', { number: job.number }) : t('jobs.deleteTitleNoNumber')} text={t('jobs.deleteText')}
                           onConfirm={remove} onClose={() => setDeleteOpen(false)} />
        </Layout>
    );
}
